import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { Box } from '@mui/material';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { EditorState } from 'lexical';
import NewMentionsPlugin from './plugins/MentionsPlugin';
import { MentionNode } from './nodes/MentionNode';
import { SubmitPlugin } from './plugins/SubmitPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback } from 'react';
import { WorkspaceUser } from '../../types/workspaceUser';
import { ImageNode } from './nodes/ImageNode';
import { ImagePlugin } from './plugins/ImagePlugin';

type CommentEditorProps = {
  onSubmit: (formattedText: string, mentions: string[], cleanMessage: string, files?: File[]) => void;
  users: WorkspaceUser[];
  type?: 'annotation' | 'comment';
}

const theme = {
  paragraph: 'editor-paragraph',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
  }
};

const initialConfig = {
  namespace: 'MyEditor',
  theme,
  onError: (error: Error) => console.error(error),
  nodes: [MentionNode, ImageNode],
  editorState: undefined,
  editable: true,
};

// tracks change and extracts the message and shows mentions dialog box etc
function OnChangeTracker() {
  const onChange = (editorState: EditorState) => {
    editorState.read(() => {
      // Get the editor state as JSON
      const rawState = editorState.toJSON();

      // Extract only the content-related parts
      // this creates a structure as follows:
      // {
      //   content: [
      //     {
      //       type: 'paragraph',
      //       children: [
      //         {
      //           type: 'text',
      //           text: 'Hello, '
      //         },
      //         {
      //           type: 'mention',
      //           mentionName: 'John Doe',
      //           mentionId: 'auth0|1234567890' 
      //         }
      //       ]
      //     }
      //   ]
      // }
      const messageStructure = {
        // go through each node in the root
        content: rawState.root.children.map((node: any) => {
          // each paragraph node will contain sub nodes of text type or others
          if (node.type === 'paragraph') {
            return {
              type: 'paragraph',
              children: node.children.map((child: any) => {
                // if the child is a mention node
                if (child.type === 'mention') {
                  return {
                    type: 'mention',
                    mentionName: child.mentionName,
                    mentionId: child.mentionId
                  };
                }

                if (child.type === 'image') {
                  return {
                    type: 'image',
                    image: child.image
                  };
                }

                // if the child is a text node
                return {
                  text: child.text,
                  format: {
                    bold: (child.format & 1) === 1,
                    italic: (child.format & 2) === 2,
                  }
                };
              })
            };
          }

          // NOTE: if the node is not a paragraph, return it as is (can be an edgecase)
          return node;
        })
      };

    });
  };

  // create the OnChange plugin built in Lexical with our custom onchange function
  return <OnChangePlugin onChange={onChange} />;
}

// this just makes sure the editor is focused when clicked anywhere on it rather than just the textbox
function FocusHandler() {
  const [editor] = useLexicalComposerContext();

  const handleClick = useCallback(() => {
    editor.focus();
  }, [editor]);

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: 'absolute',
        top: '60px',
        // this just makes the editor fill the entire box
        inset: 0,
        cursor: 'text',
        zIndex: 1
      }}
    />
  );
}

// this is the main component that renders the editor
const CommentEditor = ({ onSubmit, users }: CommentEditorProps) => {
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Box sx={{
        border: '1px solid #313131',
        width: '100%',
        borderRadius: '.25rem',
        backgroundColor: '#212121',
        position: 'relative',
      }}>
        {/* this is the toolbar that contains the formatting options and options to insert images */}
        <ToolbarPlugin />
        {/* this is the main editor */}
        <Box sx={{ p: .5, pb: 1, position: 'relative' }}>
          {/* this is to support rich text in the editor */}
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                style={{
                  minHeight: '3rem',
                  outline: 'none',
                  padding: '.5rem',
                  color: 'white',
                  position: 'relative',
                  zIndex: 1
                }}
              />
            }
            placeholder={
              <div style={{
                color: '#666',
                position: 'absolute',
                top: '50%',           // Center vertically
                transform: 'translateY(-50%)',  // Adjust for perfect centering
                left: '12px',
                pointerEvents: 'none',
                lineHeight: '1.5'     // Match the input's line height
              }}>
                Enter your comment...
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangeTracker />
          <NewMentionsPlugin users={users} />
          <SubmitPlugin onSubmit={onSubmit} />
          <FocusHandler />
          <ImagePlugin />
        </Box>
      </Box>
    </LexicalComposer>
  );
};

export default CommentEditor;